import type { DbExecutor } from "./executor";
import { generateId } from "./ids";
import { ValidationError } from "./repositories/projectRepository";
import type { FinishService } from "./finishService";
import type { CalibrationService } from "./calibrationService";
import {
  parseStandardSnapshot,
  standardSnapshotOf,
  type ProjectStandardSnapshot,
  type RenovationStandard,
  type RenovationStandardDetail,
  type StandardFinishChoice,
  type StandardRoomPackage,
  type StandardSpec,
  type StandardSurface,
} from "../domain/renovationStandard";
import { compareStandard, type StandardComparison } from "../engine/standardEvolution";
import { FINISH_MATERIALS } from "../engine/finishes";

/**
 * RENOVATION STANDARDS (spec §90) — the reusable answer to "how do I
 * normally renovate a property?".
 *
 * This service owns the workspace-level records and ONE application step.
 * It deliberately writes nothing of its own into a project's scope: applying
 * a standard sets the project's §82 finish defaults, points the project at
 * the §77 estimating profile, and freezes a SNAPSHOT of what the standard
 * said. Everything after that is the project's own rows, which is exactly
 * what makes tomorrow's edit to the standard harmless.
 */

interface StandardRow {
  id: string;
  org_id: string;
  name: string;
  notes: string;
  estimating_profile_id: string | null;
  is_default: number;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

const now = () => new Date().toISOString();

const mapStandard = (row: StandardRow): RenovationStandard => ({
  id: row.id,
  orgId: row.org_id,
  name: row.name,
  notes: row.notes,
  estimatingProfileId: row.estimating_profile_id,
  isDefault: Number(row.is_default) === 1,
  archivedAt: row.archived_at,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export interface StandardDraft {
  name: string;
  notes?: string;
  estimatingProfileId?: string | null;
}

export class RenovationStandardService {
  constructor(
    private readonly db: DbExecutor,
    private readonly finishes: FinishService,
    private readonly calibration: CalibrationService,
  ) {}

  /* ---------------- the library ---------------- */

  async list(options: { includeArchived?: boolean } = {}): Promise<RenovationStandard[]> {
    const rows = await this.db.select<StandardRow>(
      `SELECT * FROM renovation_standards
        ${options.includeArchived ? "" : "WHERE archived_at IS NULL"}
        ORDER BY is_default DESC, name`,
    );
    return rows.map(mapStandard);
  }

  async getById(id: string): Promise<RenovationStandard | null> {
    const rows = await this.db.select<StandardRow>("SELECT * FROM renovation_standards WHERE id = ?", [id]);
    return rows.length > 0 ? mapStandard(rows[0]) : null;
  }

  /** The standard a new project starts from unless somebody chooses another. */
  async defaultStandard(): Promise<RenovationStandard | null> {
    const rows = await this.db.select<StandardRow>(
      "SELECT * FROM renovation_standards WHERE is_default = 1 AND archived_at IS NULL LIMIT 1",
    );
    return rows.length > 0 ? mapStandard(rows[0]) : null;
  }

  async detail(id: string): Promise<RenovationStandardDetail | null> {
    const standard = await this.getById(id);
    if (!standard) return null;
    const [finishRows, specRows, packageRows] = await Promise.all([
      this.db.select<{ surface: string; material_key: string }>(
        "SELECT surface, material_key FROM renovation_standard_finishes WHERE standard_id = ? ORDER BY surface",
        [id],
      ),
      this.db.select<{ feature_definition_id: string; assembly_template_id: string | null; product_id: string | null; notes: string }>(
        "SELECT feature_definition_id, assembly_template_id, product_id, notes FROM renovation_standard_specs WHERE standard_id = ? ORDER BY feature_definition_id",
        [id],
      ),
      this.db.select<{ room_type_id: string; package_id: string }>(
        "SELECT room_type_id, package_id FROM renovation_standard_room_packages WHERE standard_id = ? ORDER BY room_type_id",
        [id],
      ),
    ]);
    return {
      standard,
      finishes: finishRows.map((r) => ({ surface: r.surface as StandardSurface, materialKey: r.material_key })),
      specs: specRows.map((r) => ({
        featureDefinitionId: r.feature_definition_id,
        assemblyTemplateId: r.assembly_template_id,
        productId: r.product_id,
        notes: r.notes,
      })),
      roomPackages: packageRows.map((r) => ({ roomTypeId: r.room_type_id, packageId: r.package_id })),
    };
  }

  async create(draft: StandardDraft): Promise<RenovationStandard> {
    if (draft.name.trim() === "") throw new ValidationError(["Give the standard a name."]);
    const stamp = now();
    const id = generateId();
    const existing = await this.list();
    await this.db.execute(
      `INSERT INTO renovation_standards (id, org_id, name, notes, estimating_profile_id, is_default, archived_at, created_at, updated_at)
       VALUES (?, 'default', ?, ?, ?, ?, NULL, ?, ?)`,
      [id, draft.name.trim(), draft.notes ?? "", draft.estimatingProfileId ?? null, existing.length === 0 ? 1 : 0, stamp, stamp],
    );
    return (await this.getById(id))!;
  }

  async update(id: string, draft: StandardDraft): Promise<void> {
    if (draft.name.trim() === "") throw new ValidationError(["Give the standard a name."]);
    await this.db.execute(
      "UPDATE renovation_standards SET name = ?, notes = ?, estimating_profile_id = ?, updated_at = ? WHERE id = ?",
      [draft.name.trim(), draft.notes ?? "", draft.estimatingProfileId ?? null, now(), id],
    );
  }

  /** One default per workspace; choosing a new one clears the old. */
  async setDefault(id: string): Promise<void> {
    await this.db.transaction(async () => {
      await this.db.execute("UPDATE renovation_standards SET is_default = 0, updated_at = ? WHERE is_default = 1", [now()]);
      await this.db.execute("UPDATE renovation_standards SET is_default = 1, updated_at = ? WHERE id = ?", [now(), id]);
    });
  }

  /**
   * ARCHIVE, NEVER DELETE (spec §90-54): a project created from this
   * standard keeps its own snapshot, and the name it started from stays
   * readable. Archiving only takes it out of the pickers.
   */
  async archive(id: string): Promise<void> {
    await this.db.execute(
      "UPDATE renovation_standards SET archived_at = ?, is_default = 0, updated_at = ? WHERE id = ?",
      [now(), now(), id],
    );
  }

  async unarchive(id: string): Promise<void> {
    await this.db.execute("UPDATE renovation_standards SET archived_at = NULL, updated_at = ? WHERE id = ?", [now(), id]);
  }

  /** A copy to change freely — the way a second standard usually starts. */
  async duplicate(id: string, name: string): Promise<RenovationStandard> {
    const detail = await this.detail(id);
    if (!detail) throw new ValidationError(["That standard no longer exists."]);
    const copy = await this.create({ name, notes: detail.standard.notes, estimatingProfileId: detail.standard.estimatingProfileId });
    await this.db.transaction(async () => {
      for (const finish of detail.finishes) await this.setFinish(copy.id, finish.surface, finish.materialKey);
      for (const spec of detail.specs) await this.setSpec(copy.id, spec);
      for (const pkg of detail.roomPackages) await this.setRoomPackage(copy.id, pkg.roomTypeId, pkg.packageId);
    });
    return copy;
  }

  /* ---------------- what a standard contains ---------------- */

  async setFinish(standardId: string, surface: StandardSurface, materialKey: string | null): Promise<void> {
    if (materialKey === null) {
      await this.db.execute("DELETE FROM renovation_standard_finishes WHERE standard_id = ? AND surface = ?", [standardId, surface]);
      return;
    }
    const existing = await this.db.select<{ surface: string }>(
      "SELECT surface FROM renovation_standard_finishes WHERE standard_id = ? AND surface = ?",
      [standardId, surface],
    );
    if (existing.length > 0) {
      await this.db.execute("UPDATE renovation_standard_finishes SET material_key = ? WHERE standard_id = ? AND surface = ?", [materialKey, standardId, surface]);
    } else {
      await this.db.execute(
        "INSERT INTO renovation_standard_finishes (standard_id, surface, material_key, created_at) VALUES (?, ?, ?, ?)",
        [standardId, surface, materialKey, now()],
      );
    }
    await this.touch(standardId);
  }

  /**
   * A SPECIFICATION: which assembly prices this kind of work, and
   * optionally the product the company buys. Passing both as null removes
   * the standard's opinion about that work.
   */
  async setSpec(standardId: string, spec: StandardSpec): Promise<void> {
    if (spec.assemblyTemplateId === null && spec.productId === null) {
      await this.db.execute(
        "DELETE FROM renovation_standard_specs WHERE standard_id = ? AND feature_definition_id = ?",
        [standardId, spec.featureDefinitionId],
      );
      await this.touch(standardId);
      return;
    }
    const stamp = now();
    const existing = await this.db.select<{ feature_definition_id: string }>(
      "SELECT feature_definition_id FROM renovation_standard_specs WHERE standard_id = ? AND feature_definition_id = ?",
      [standardId, spec.featureDefinitionId],
    );
    if (existing.length > 0) {
      await this.db.execute(
        "UPDATE renovation_standard_specs SET assembly_template_id = ?, product_id = ?, notes = ?, updated_at = ? WHERE standard_id = ? AND feature_definition_id = ?",
        [spec.assemblyTemplateId, spec.productId, spec.notes, stamp, standardId, spec.featureDefinitionId],
      );
    } else {
      await this.db.execute(
        `INSERT INTO renovation_standard_specs (standard_id, feature_definition_id, assembly_template_id, product_id, notes, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [standardId, spec.featureDefinitionId, spec.assemblyTemplateId, spec.productId, spec.notes, stamp, stamp],
      );
    }
    await this.touch(standardId);
  }

  async setRoomPackage(standardId: string, roomTypeId: string, packageId: string | null): Promise<void> {
    if (packageId === null) {
      await this.db.execute("DELETE FROM renovation_standard_room_packages WHERE standard_id = ? AND room_type_id = ?", [standardId, roomTypeId]);
      await this.touch(standardId);
      return;
    }
    const existing = await this.db.select<{ room_type_id: string }>(
      "SELECT room_type_id FROM renovation_standard_room_packages WHERE standard_id = ? AND room_type_id = ?",
      [standardId, roomTypeId],
    );
    if (existing.length > 0) {
      await this.db.execute("UPDATE renovation_standard_room_packages SET package_id = ? WHERE standard_id = ? AND room_type_id = ?", [packageId, standardId, roomTypeId]);
    } else {
      await this.db.execute(
        "INSERT INTO renovation_standard_room_packages (standard_id, room_type_id, package_id, created_at) VALUES (?, ?, ?, ?)",
        [standardId, roomTypeId, packageId, now()],
      );
    }
    await this.touch(standardId);
  }

  private async touch(standardId: string): Promise<void> {
    await this.db.execute("UPDATE renovation_standards SET updated_at = ? WHERE id = ?", [now(), standardId]);
  }

  /* ---------------- the project side ---------------- */

  /**
   * APPLY ONCE, THEN THE PROJECT OWNS IT (spec §90-7).
   *
   * Writes the project's §82 finish defaults, points it at the standard's
   * §77 pricing profile, and freezes the snapshot. No feature, assembly or
   * cost line is created here: rooms get their scope from the ordinary
   * package application, and the finishes materialise through §82's own
   * idempotent apply once rooms exist.
   */
  async applyToProject(projectId: string, standardId: string): Promise<ProjectStandardSnapshot> {
    const detail = await this.detail(standardId);
    if (!detail) throw new ValidationError(["That standard no longer exists."]);
    const snapshot = standardSnapshotOf(detail, now());
    snapshot.automaticFinishVersion = 1;
    await this.db.transaction(async () => {
      for (const finish of snapshot.finishes) {
        await this.finishes.setProjectFinish(projectId, null, finish.surface, finish.materialKey);
      }
      if (snapshot.estimatingProfileId) await this.calibration.setProjectProfile(projectId, snapshot.estimatingProfileId);
      await this.db.execute(
        `UPDATE projects SET renovation_standard_id = ?, renovation_standard_name = ?,
           renovation_standard_snapshot_json = ?, updated_at = ? WHERE id = ?`,
        [snapshot.standardId, snapshot.name, JSON.stringify(snapshot), now(), projectId],
      );
      const active = await this.db.select<{ active_design_id: string | null }>("SELECT active_design_id FROM projects WHERE id = ?", [projectId]);
      await this.finishes.reconcileAutomatic(projectId, active[0]?.active_design_id ?? null);
    });
    return snapshot;
  }

  /** What this project froze when it was created (null = no standard). */
  async projectSnapshot(projectId: string): Promise<ProjectStandardSnapshot | null> {
    const rows = await this.db.select<{ renovation_standard_snapshot_json: string | null }>(
      "SELECT renovation_standard_snapshot_json FROM projects WHERE id = ?",
      [projectId],
    );
    return parseStandardSnapshot(rows[0]?.renovation_standard_snapshot_json ?? null);
  }

  /**
   * The specification this project's standard has for a kind of work — the
   * assembly to attach when the user has not chosen one. Consulted BELOW any
   * project-specific choice, never above it.
   */
  async specForProject(projectId: string, featureDefinitionId: string | null): Promise<StandardSpec | null> {
    if (!featureDefinitionId) return null;
    const snapshot = await this.projectSnapshot(projectId);
    return snapshot?.specs.find((s) => s.featureDefinitionId === featureDefinitionId) ?? null;
  }

  /** The room package this project's standard uses for a room type. */
  async roomPackageForProject(projectId: string, roomTypeId: string | null): Promise<StandardRoomPackage | null> {
    if (!roomTypeId) return null;
    const snapshot = await this.projectSnapshot(projectId);
    const found = snapshot?.roomPackages.find((r) => r.roomTypeId === roomTypeId);
    return found ?? null;
  }

  /** Every finish the project's standard chose (for display and for §82). */
  async finishesForProject(projectId: string): Promise<StandardFinishChoice[]> {
    return (await this.projectSnapshot(projectId))?.finishes ?? [];
  }

  /* ---------------- what this house taught me (spec §91-11/12) ---------------- */

  /**
   * WHAT CHANGED BETWEEN THE STANDARD AND THIS PROJECT.
   *
   * Reads the project's FINAL reusable configuration — its §82 project
   * finishes, the assembly its work items actually ended up using for each
   * kind of work, and the package each kind of room actually received — and
   * puts it beside the snapshot the project started from. Nothing is
   * written: the answer is a preview for a person to read.
   *
   * WHAT IS DELIBERATELY NOT LOOKED AT: quantities, dimensions, money,
   * quotes, purchases, schedule, geometry. A standard cannot hold them, so
   * they are not even candidates (spec §91-14, pinned by test).
   */
  async compareProject(projectId: string): Promise<StandardComparison> {
    const snapshot = await this.projectSnapshot(projectId);
    const standard = snapshot ? await this.getById(snapshot.standardId) : null;
    const designId = await this.activeDesignId(projectId);

    const [finishRows, overrideRows, specRows, packageRows, assemblyRows, suggestionRows, packageNameRows] = await Promise.all([
      this.db.select<{ surface: string; material_key: string }>(
        `SELECT surface, material_key FROM project_finish_defaults
          WHERE project_id = ? AND (design_id IS ? OR design_id = ?) AND enabled = 1`,
        [projectId, designId, designId],
      ),
      this.db.select<{ surface: string; material_key: string; room_name: string }>(
        `SELECT o.surface, o.material_key, r.name AS room_name
           FROM room_finish_overrides o JOIN rooms r ON r.id = o.room_id
          WHERE o.project_id = ? AND (o.design_id IS ? OR o.design_id = ?) AND o.excluded = 0`,
        [projectId, designId, designId],
      ),
      /*
       * The specification the project actually used: the assembly attached
       * to its live work items, per kind of work. The DISTINCT pairs are
       * read and counted here rather than with a window function, which
       * SQLite does not support over DISTINCT (and which would not be
       * portable anyway). A kind of work priced two different ways reports
       * as several rather than having one of them picked for it.
       */
      this.db.select<{ feature_definition_id: string; definition_name: string; template_id: string }>(
        `SELECT DISTINCT f.feature_definition_id,
                d.display_name AS definition_name,
                i.template_id
           FROM project_features f
           JOIN feature_definitions d ON d.id = f.feature_definition_id
           JOIN assembly_instances i ON i.project_feature_id = f.id
          WHERE f.project_id = ? AND f.archived_at IS NULL
            AND (f.design_id IS NULL OR f.design_id = ?)
            AND f.feature_definition_id IS NOT NULL
            AND i.archived_at IS NULL`,
        [projectId, designId],
      ),
      this.db.select<{ room_type_id: string; room_type_name: string; package_id: string }>(
        `SELECT DISTINCT r.room_type_id,
                rt.display_name AS room_type_name,
                a.package_id
           FROM package_applications a
           JOIN rooms r ON r.id = a.room_id
           JOIN room_types rt ON rt.id = r.room_type_id
          WHERE a.project_id = ? AND r.archived_at IS NULL AND r.room_type_id IS NOT NULL`,
        [projectId],
      ),
      this.db.select<{ id: string; name: string }>("SELECT id, display_name AS name FROM assembly_templates"),
      /* Phase 8.1's single-suggestion rule: where a kind of work has exactly
       * one suggested assembly, attaching it was no decision at all. */
      this.db.select<{ feature_definition_id: string; template_id: string }>(
        "SELECT feature_definition_id, template_id FROM assembly_suggestions",
      ),
      this.db.select<{ id: string; name: string }>("SELECT id, name FROM scope_packages"),
    ]);

    return compareStandard({
      snapshot,
      standardExists: standard !== null && standard.archivedAt === null,
      projectFinishes: finishRows.map((r) => ({ surface: r.surface as StandardSurface, materialKey: r.material_key })),
      roomOverrides: overrideRows.map((r) => ({
        surface: r.surface as StandardSurface,
        materialKey: r.material_key,
        roomLabel: r.room_name,
      })),
      projectSpecs: specRows.map((r) => ({
        featureDefinitionId: r.feature_definition_id,
        definitionName: r.definition_name,
        assemblyTemplateId: r.template_id,
        distinctCount: specRows.filter((o) => o.feature_definition_id === r.feature_definition_id).length,
      })),
      projectRoomPackages: packageRows.map((r) => ({
        roomTypeId: r.room_type_id,
        roomTypeName: r.room_type_name,
        packageId: r.package_id,
        distinctCount: packageRows.filter((o) => o.room_type_id === r.room_type_id).length,
      })),
      impliedSpecs: onlySuggestion(suggestionRows),
      materialLabels: new Map(FINISH_MATERIALS.map((m) => [m.key, m.displayName])),
      assemblyNames: new Map(assemblyRows.map((r) => [r.id, r.name])),
      packageNames: new Map(packageNameRows.map((r) => [r.id, r.name])),
    });
  }

  /**
   * SAVE THE CHOICES I TICKED (spec §91-12).
   *
   * Explicit, selective and one-directional: only the differences the user
   * named are written, only into the standard's own reusable slots, and only
   * when the comparison OFFERED them (a room exception or an ambiguous
   * specification is refused by id rather than quietly skipped). The project
   * is not touched at all — its snapshot, scope, estimate, quotes and
   * actuals are exactly as they were, which is what makes this safe to run
   * on a finished house (spec §91-15, test-pinned).
   */
  async saveBackToStandard(projectId: string, changeIds: readonly string[]): Promise<StandardSaveBackResult> {
    const comparison = await this.compareProject(projectId);
    if (comparison.standardId === null) throw new ValidationError(["This project did not start from a renovation standard."]);
    if (!comparison.standardExists) {
      throw new ValidationError(["That renovation standard has been archived or removed, so there is nothing to update."]);
    }
    const wanted = new Set(changeIds);
    const saved: string[] = [];
    const refused: Array<{ id: string; reason: string }> = [];
    const chosen = comparison.changes.filter((c) => wanted.has(c.id));
    for (const id of wanted) {
      if (!chosen.some((c) => c.id === id)) refused.push({ id, reason: "That difference is no longer in this project." });
    }
    const writes = chosen.filter((change) => {
      if (change.offered && change.write) return true;
      refused.push({ id: change.id, reason: change.note });
      return false;
    });
    if (writes.length === 0) return { standardId: comparison.standardId, saved, refused };

    const standardId = comparison.standardId;
    await this.db.transaction(async () => {
      for (const change of writes) {
        const write = change.write!;
        if (write.kind === "finish") await this.setFinish(standardId, write.surface, write.materialKey);
        else if (write.kind === "spec") {
          const existing = (await this.detail(standardId))?.specs.find((s) => s.featureDefinitionId === write.featureDefinitionId);
          // The PRODUCT preference is the standard's own and is left alone:
          // §90's specification-not-SKU rule (spec §91-36).
          await this.setSpec(standardId, {
            featureDefinitionId: write.featureDefinitionId,
            assemblyTemplateId: write.assemblyTemplateId,
            productId: existing?.productId ?? null,
            notes: existing?.notes ?? "",
          });
        } else await this.setRoomPackage(standardId, write.roomTypeId, write.packageId);
        saved.push(change.id);
      }
    });
    return { standardId, saved, refused };
  }

  private async activeDesignId(projectId: string): Promise<string | null> {
    const rows = await this.db.select<{ active_design_id: string | null }>(
      "SELECT active_design_id FROM projects WHERE id = ?",
      [projectId],
    );
    return rows[0]?.active_design_id ?? null;
  }
}

/** The one suggested assembly per kind of work, where there is exactly one. */
function onlySuggestion(rows: ReadonlyArray<{ feature_definition_id: string; template_id: string }>): Map<string, string> {
  const byDefinition = new Map<string, string[]>();
  for (const row of rows) {
    const list = byDefinition.get(row.feature_definition_id) ?? [];
    list.push(row.template_id);
    byDefinition.set(row.feature_definition_id, list);
  }
  const out = new Map<string, string>();
  for (const [definitionId, templates] of byDefinition) if (templates.length === 1) out.set(definitionId, templates[0]);
  return out;
}

export interface StandardSaveBackResult {
  standardId: string;
  /** Change ids actually written. */
  saved: string[];
  /** Change ids that were asked for and refused, each with the reason. */
  refused: Array<{ id: string; reason: string }>;
}
